'use client';

import { useFiltersContext } from '@components/FilterPage/Context/useFiltersContext';
import {
  Button,
  Flex,
  FormHelperText,
  Input,
  InputAdornment,
  Typography
} from '@coolblue-development/freddy';
import confirmHoursFilterTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/confirmHoursFilterTrigger';
import selectHoursFilterTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/selectHoursFilterTrigger';
import { ArrowRight } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';
import { FilterCategories } from '@server/careers-backend/search';
import React, { useState } from 'react';

type Props = {
  label: string | undefined;
  filterCategory: FilterCategories;
  minOption: number;
  maxOption: number;
  isMobileVersion: boolean;
};

export const FilterFacetNumberItem = ({
  label,
  filterCategory,
  minOption,
  maxOption,
  isMobileVersion
}: Props) => {
  const { t } = useTranslation(`filterPage.filterFacets.${filterCategory}`);
  const { trackEvent } = useAnalytics();
  const { getSelectedFilterOptions, setFilter } = useFiltersContext();

  const subFeature = isMobileVersion ? 'filter_overlay' : 'filter_list';

  const [inputField, setInputField] = useState<string | undefined>(
    getSelectedFilterOptions(filterCategory)[0] || undefined
  );
  const [error, setError] = useState<string>();
  const helperText = t('helperText', { min: minOption, max: maxOption });

  const validate = () => {
    const numberInput = Number(inputField);

    if (!inputField && getSelectedFilterOptions(filterCategory)[0] !== undefined) {
      setError(undefined);
      setFilter(filterCategory, undefined);
    } else if (!numberInput || Number.isNaN(numberInput)) {
      setError(t('validation.valueBetween', { min: minOption, max: maxOption }));
    } else if (numberInput < minOption) {
      setError(t('validation.minimum', { min: minOption }));
    } else if (numberInput > maxOption) {
      setError(t('validation.maximum', { max: maxOption }));
    } else {
      setError(undefined);
      setFilter(filterCategory, inputField ? [inputField] : undefined);
    }
    if (filterCategory === 'hours')
      trackEvent(
        confirmHoursFilterTrigger({
          sub_feature: subFeature,
          filter_value: inputField || '',
          number_of_items: 1, // Todo: replace with pagination count
          is_recommended: true
        })
      );
  };

  return (
    <Flex direction='column'>
      {label && (
        <Typography component='label' htmlFor={`${filterCategory}-input`} variant='h3' mb={200}>
          {label}
        </Typography>
      )}
      <Flex direction='column' gap={100}>
        <Flex gap={200}>
          <Input
            id={`${filterCategory}-input`}
            aria-describedby={`${filterCategory}-helper-text`}
            endAdornment={<InputAdornment>{t(filterCategory)}</InputAdornment>}
            validation={error ? 'error' : undefined}
            value={inputField}
            onChange={e => {
              setInputField(e.target.value);
              setError(undefined);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                validate();
              }
            }}
            onFocus={() =>
              filterCategory === 'hours' &&
              trackEvent(
                selectHoursFilterTrigger({
                  sub_feature: subFeature,
                  input_field: filterCategory,
                  status: error ? 'failure' : 'success',
                  content: error || helperText
                })
              )
            }
            sx={{ width: '10rem' }}
          />
          <Button variant='secondary' size='small' type='button' onClick={validate}>
            <ArrowRight />
          </Button>
        </Flex>
        {error ? (
          <FormHelperText validation='error' id={`${filterCategory}-helper-text`}>
            {error}
          </FormHelperText>
        ) : (
          <FormHelperText id={`${filterCategory}-helper-text`}>{helperText}</FormHelperText>
        )}
      </Flex>
    </Flex>
  );
};
